import axios from "axios";
import { getCustomAxiosError, getCustomOmegaError } from "@src/helpers/error/localesError";

/**
 * Преобразует transport/network ошибку Axios в `Error` с локализованным сообщением.
 * @param error — ошибка запроса
 */
export function axiosErrorHandler(error: unknown): never {
    if (!axios.isAxiosError(error)) {
        if (error instanceof Error) {
            throw error;
        }
        throw new Error(String(error));
    }

    console.error(error);

    if (error.code != null) {
        const customError = getCustomAxiosError(error.code);
        if (customError != null) {
            throw new Error(customError);
        }
        throw new Error(error.message);
    }

    if (error.response != null) {
        const customError = getCustomAxiosError(error.response.status.toString());
        if (customError != null) {
            throw new Error(customError);
        }
        throw new Error(error.message);
    }

    throw new Error(error.message);
}

interface OmegaErrorInfo {
    Code: number;
    String: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === "object" && !Array.isArray(value);
}

function isOmegaErrorInfo(value: unknown): value is OmegaErrorInfo {
    if (!isRecord(value)) {
        return false;
    }
    return typeof value.Code === "number" && typeof value.String === "string";
}

/**
 * Извлекает объект ошибки Omega из тела ответа.
 */
export function getOmegaErrorFromResponseData(data: unknown): OmegaErrorInfo | null {
    if (!isRecord(data)) {
        return null;
    }
    const errorField = data.error;
    if (!isOmegaErrorInfo(errorField)) {
        return null;
    }
    return errorField;
}

const OMEGA_ERROR_CODE_MAP: Record<number, string> = {
    2002: "Неправильный логин или пароль",
    4001: "Метод обращения к этой функции не найден в текущей версии REST API сервера",
    4002: "Не указан ключ аутентификации пользователя",
};

/**
 * Проверяет ответ Omega и бросает `Error` при `error.Code !== 0`.
 * @param data — тело ответа
 */
export function assertOmegaResponseOk(data: unknown): void {
    const omegaError = getOmegaErrorFromResponseData(data);
    if (omegaError == null) {
        return;
    }

    const { Code: errorCode, String: errorMessage } = omegaError;
    const hasError = !Number.isNaN(errorCode) && errorCode !== 0;

    if (!hasError) {
        return;
    }

    const customErrorByCode = OMEGA_ERROR_CODE_MAP[errorCode] ?? getCustomOmegaError(errorCode.toString());
    if (customErrorByCode != null) {
        throw new Error(customErrorByCode);
    }

    const normalizedMessage =
        typeof errorMessage === "string" && errorMessage.trim().length > 0
            ? errorMessage.trim()
            : `Ошибка Omega (code: ${errorCode})`;

    throw new Error(normalizedMessage);
}

/**
 * Возвращает код Omega-ошибки из тела ответа, если есть.
 */
export function getOmegaErrorCode(data: unknown): number | null {
    const omegaError = getOmegaErrorFromResponseData(data);
    if (omegaError == null) {
        return null;
    }
    if (Number.isNaN(omegaError.Code) || omegaError.Code === 0) {
        return null;
    }
    return omegaError.Code;
}
